import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RandomDotmSpinner } from '@/components/ui/RandomDotmSpinner';
import { Button } from '../../../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../../components/ui/dialog';
import { JsonShapePreview } from '../JsonShapePreview';
import { JsonStructureInspector } from '../meta-info/JsonStructureInspector';
import { inferJsonStructureFromValues } from '../../utils/inferJsonStructure';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';
import type { JsonStructureCache } from '@/types/electron';
import { DocsLinkButton } from '@/features/help-feedback';

type DeclareJsonColumnDialogProps = {
  open: boolean;
  schema: string;
  table: string;
  column: string;
  /** Underlying SQL data type (e.g. `nvarchar(max)`). Surfaced under the
   * description so the user knows what the column is declared as before
   * opting into JSON semantics. */
  dataType?: string;
  /** Pretty engine label to mention which validity guard will be used. */
  engineLabel?: string;
  /** Parent-controlled error to surface (e.g. apply-time IPC failure). */
  errorMessage?: string | null;
  /** When set, blocks confirmation and surfaces this message as a permanent
   * banner — used to explain that the active server version doesn't support
   * declare-on-text JSON (e.g. PostgreSQL <16). */
  unsupportedReason?: string | null;
  /** Loaded column values for the row sample. Used to infer the column's
   * JSON shape so the user can decide with confidence. Replaces the older
   * single-value `sampleValue` API. */
  sampleRows?: ReadonlyArray<unknown>;
  /** Refetch context — when present, the dialog renders a Refresh + Filter
   * affordance so the user can sample a larger or filtered set of rows
   * before deciding to declare. Optional; pre-declare with only loaded
   * sample rows still works when these are omitted. */
  connectionId?: string;
  database?: string;
  engine?: DatabaseEngineId;
  /**
   * Copy/flow variant. `'eligible'` (default) frames this as "enable JSON
   * features on a text column" and runs the inferred-shape preview as
   * persuasion. `'native'` frames it as "save path metadata + loose-FK
   * targets on a column that's already JSON" and tones down the validity
   * messaging.
   */
  mode?: 'eligible' | 'native';
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
};

const CONFIRM_TIMEOUT_MS = 10_000;
const SAMPLE_MAX_CHARS = 600;

/** Pick the first non-empty raw string from the sample list so we can
 * show a literal fallback when nothing in the column looked like JSON. */
function pickFirstRawSample(values: ReadonlyArray<unknown>): string | null {
  for (const v of values) {
    if (v === null || v === undefined) continue;
    const str = typeof v === 'string' ? v : String(v);
    if (str.trim().length === 0) continue;
    return str;
  }
  return null;
}

function truncate(text: string): string {
  return text.length > SAMPLE_MAX_CHARS
    ? text.slice(0, SAMPLE_MAX_CHARS) + '…'
    : text;
}

/**
 * Confirmation dialog shown when the user picks the `json` operator on
 * a `json-eligible` (long-text, undeclared) column. Renders the inferred
 * shape across all loaded rows so the user can eyeball "the column has
 * consistent structure" vs. "this isn't JSON". MetaInfo persistence
 * happens at filter-apply time so cancelled drafts don't leave a sticky
 * declaration behind.
 */
export function DeclareJsonColumnDialog({
  open,
  schema,
  table,
  column,
  dataType,
  engineLabel,
  errorMessage,
  unsupportedReason,
  sampleRows,
  connectionId,
  database,
  engine,
  mode = 'eligible',
  onConfirm,
  onCancel,
}: DeclareJsonColumnDialogProps) {
  const inferred = useMemo(
    () => inferJsonStructureFromValues(sampleRows ?? []),
    [sampleRows],
  );
  const hasRefetchContext = Boolean(connectionId && database && engine);
  const initialCacheForInspector: JsonStructureCache | null = useMemo(() => {
    if (!hasRefetchContext) return null;
    return {
      schema,
      table,
      column,
      paths: inferred.paths,
      sampledRows: inferred.sampledRows,
      sampledAt: new Date().toISOString(),
    };
  }, [hasRefetchContext, schema, table, column, inferred.paths, inferred.sampledRows]);
  const rawFallback = useMemo(
    () => (sampleRows ? pickFirstRawSample(sampleRows) : null),
    [sampleRows],
  );
  const hasInputRows = (sampleRows?.length ?? 0) > 0;
  const noJsonInferred = hasInputRows && inferred.jsonRows === 0;
  const fallbackText =
    noJsonInferred && rawFallback ? truncate(rawFallback) : null;

  const confirmRef = useRef<HTMLButtonElement>(null);
  const [confirming, setConfirming] = useState(false);
  const [timeoutError, setTimeoutError] = useState<string | null>(null);
  // M4 reentrancy guard. `inflightRef` blocks a second click while an
  // earlier `onConfirm()` is still pending — even after the 10s timeout
  // cleared the spinner. `tokenRef` makes stale resolutions (e.g. promise
  // settles after the dialog closed) drop silently.
  const inflightRef = useRef(false);
  const tokenRef = useRef<symbol | null>(null);

  useEffect(() => {
    if (open) {
      setConfirming(false);
      setTimeoutError(null);
      const t = setTimeout(() => confirmRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
    tokenRef.current = null;
    inflightRef.current = false;
  }, [open]);

  const handleConfirm = useCallback(async () => {
    if (inflightRef.current) return;
    const myToken = Symbol('declare-json-confirm');
    tokenRef.current = myToken;
    inflightRef.current = true;

    setConfirming(true);
    setTimeoutError(null);

    let timedOut = false;
    const timer = setTimeout(() => {
      if (tokenRef.current !== myToken) return;
      timedOut = true;
      setConfirming(false);
      setTimeoutError(
        'Declaration is taking longer than expected. Check your connection and try again.',
      );
    }, CONFIRM_TIMEOUT_MS);

    try {
      await onConfirm();
    } catch {
      // Parent surfaces its own error via the `errorMessage` prop. We just
      // make sure the spinner clears so the user can retry or cancel.
    } finally {
      clearTimeout(timer);
      if (tokenRef.current === myToken) {
        inflightRef.current = false;
        if (!timedOut) setConfirming(false);
      }
    }
  }, [onConfirm]);

  const effectiveUnsupportedReason = mode === 'native' ? null : unsupportedReason;
  const banner = effectiveUnsupportedReason ?? errorMessage ?? timeoutError;
  const blocked = Boolean(effectiveUnsupportedReason);

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !o && onCancel()}
    >
      <DialogContent
        className={hasRefetchContext ? 'sm:max-w-2xl' : 'sm:max-w-md'}
        data-testid="declare-json-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-1.5">
            <DialogTitle>
              {mode === 'native' ? 'Configure JSON column' : 'Declare column as JSON?'}
            </DialogTitle>
            <DocsLinkButton
              url="https://jamsql.com/docs/json-columns/"
              doc="json-columns"
            />
          </div>
          <DialogDescription>
            {mode === 'native' ? (
              <>
                <code className="font-mono text-xs" translate="no">
                  {schema}.{table}.{column}
                </code>{' '}
                is already a native JSON type. Saving here persists JSON path
                settings and loose-foreign-key targets for this column.
              </>
            ) : (
              <>
                <span translate="no" className="font-mono text-xs">
                  {schema}.{table}.{column}
                </span>{' '}
                {/* U31 (A+B merge): an Azure Storage table has String properties, not long-text columns. */}
                {engine === 'azurestorage'
                  ? 'is a text property. Declaring it as JSON enables JSONPath filtering in the entity grid and the collapsible-tree cell view.'
                  : 'is a long-text column. Declaring it as JSON enables JSONPath filtering in Table Explorer and the collapsible-tree cell view.'}
                {(dataType || engineLabel) && (
                  <>
                    <br />
                    <span
                      translate="no"
                      className="font-mono text-xs"
                      data-testid="declare-json-datatype"
                    >
                      {dataType ?? 'unknown type'}
                      {engineLabel ? ` · ${engineLabel}` : ''}
                    </span>
                  </>
                )}
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        {hasRefetchContext ? (
          <div data-testid="declare-json-sample" className="space-y-1">
            <p
              className={
                inferred.jsonRows === 0
                  ? 'text-xs text-muted-foreground italic'
                  : 'text-xs text-muted-foreground'
              }
              data-testid="declare-json-sample-population"
            >
              {inferred.jsonRows} of {inferred.sampledRows} loaded rows look like JSON
            </p>
            {noJsonInferred && (
              <p
                className="text-xs text-muted-foreground"
                data-testid="declare-json-sample-warning"
              >
                Sample doesn't look like JSON — declaring it anyway may not
                produce useful filter results.
              </p>
            )}
            <JsonStructureInspector
              schema={schema}
              table={table}
              column={column}
              initialCache={initialCacheForInspector}
              connectionId={connectionId}
              database={database}
              engine={engine}
              source="declare-dialog"
              maxPreviewHeight="160px"
            />
          </div>
        ) : hasInputRows && (
          <div data-testid="declare-json-sample" className="space-y-1">
            <p
              className={
                inferred.jsonRows === 0
                  ? 'text-xs text-muted-foreground italic'
                  : 'text-xs text-muted-foreground'
              }
              data-testid="declare-json-sample-population"
            >
              {inferred.jsonRows} of {inferred.sampledRows} loaded rows look like JSON
            </p>
            {noJsonInferred && (
              <p
                className="text-xs text-muted-foreground"
                data-testid="declare-json-sample-warning"
              >
                Sample doesn't look like JSON — declaring it anyway may not
                produce useful filter results.
              </p>
            )}
            {inferred.paths.length > 0 ? (
              <JsonShapePreview
                paths={inferred.paths}
                defaultExpandDepth={1}
                maxHeight="160px"
              />
            ) : fallbackText ? (
              <pre
                className="max-h-24 overflow-auto rounded-md border bg-muted/30 p-2 font-mono text-xs whitespace-pre-wrap break-all"
                data-testid="declare-json-sample-pre"
              >
                <span translate="no">{fallbackText}</span>
              </pre>
            ) : null}
          </div>
        )}
        {banner && (
          <div
            role="alert"
            data-testid="declare-json-error"
            className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive"
          >
            {banner}
          </div>
        )}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onCancel}
            data-testid="declare-json-cancel"
          >
            Cancel
          </Button>
          <Button
            ref={confirmRef}
            onClick={handleConfirm}
            disabled={confirming || blocked}
            data-testid="declare-json-confirm"
          >
            {confirming && (
              <RandomDotmSpinner size={14} className="mr-2" />
            )}
            {confirming ? 'Declaring…' : 'Declare as JSON'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
